import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useMemo } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { QuoteSummary, QuoteSummaryItem } from "../../models";
import { useClipProducts } from "../../catalogue/use-clip-products";
import { buildSummaryRowIndex } from "../../catalogue/catalogue-utils";
import { IncludedLayersTooltip } from "../../components/included-layers-tooltip";

interface SummaryTableProps {
  quoteSummary: QuoteSummary;
  showFeatures: boolean;
  showRate: boolean;
  showCredits: boolean;
  showTotal: boolean;
}

export const SummaryTable = (props: SummaryTableProps) => {
  const sold = props.quoteSummary.sold;
  const productsQuery = useClipProducts();
  const catalogueLoading = productsQuery.isLoading && !sold;
  const rowIndex = useMemo(
    () => buildSummaryRowIndex(productsQuery.data ?? []),
    [productsQuery.data],
  );

  return (
    <Table className="table-fixed">
      <TableHeader>
        <TableRow>
          <TableHead className="font-bold px-0 w-2/5">Product</TableHead>
          <TableHead className="font-bold px-0 text-right w-1/4">
            {props.showFeatures && "Features"}
          </TableHead>
          <TableHead className="font-bold px-0 text-right w-[15%]">
            {props.showRate && "Rate"}
          </TableHead>
          <TableHead className="font-bold px-0 text-right w-1/5">
            {props.showCredits && "Credits"}
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {props.quoteSummary.items.map((item: QuoteSummaryItem) => {
          const meta = rowIndex.get(item.attribute);
          // A sold row whose stored name isn't today's label was priced against an
          // older vocabulary, so its layer list would be another product's.
          const staleSoldRow = sold && item.name !== meta?.label;
          const showIncludes =
            !staleSoldRow && (meta?.includedLayers.length ?? 0) > 1;
          return (
            <TableRow key={item.key}>
              <TableCell className="px-0 whitespace-normal wrap-break-word">
                {catalogueLoading ? (
                  <Skeleton className="h-4 w-28" />
                ) : (
                  <span className="flex items-center gap-1">
                    <span>{sold ? item.name : (meta?.label ?? item.name)}</span>
                    {showIncludes && meta && (
                      <IncludedLayersTooltip
                        layerNames={meta.includedLayers}
                        className="size-3.5"
                      />
                    )}
                  </span>
                )}
              </TableCell>
              <TableCell className="px-0 text-right">
                {props.showFeatures && item.features.toLocaleString()}
              </TableCell>
              <TableCell className="px-0 text-right">
                {props.showRate && item.rate.toLocaleString()}
              </TableCell>
              <TableCell className="px-0 text-right">
                {props.showCredits && item.credits.toLocaleString()}
              </TableCell>
            </TableRow>
          );
        })}
        {props.showTotal && (
          <TableRow>
            <TableCell className="px-0 text-right font-bold">
              {(props.showCredits || props.showFeatures) && "Total"}
            </TableCell>
            <TableCell className="px-0 text-right font-bold">
              {props.showFeatures &&
                props.quoteSummary.totalFeatures.toLocaleString()}
            </TableCell>
            <TableCell className="px-0 text-right font-bold" />
            <TableCell className="px-0 text-right font-bold">
              {props.showCredits &&
                props.quoteSummary.totalCredits.toLocaleString()}
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
};
